import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";

export function IconInput({ icon: Icon, className = "", ...props }) {
  return (
    <div className="relative">
      <Icon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
      <Input {...props} className={`pl-10 ${className}`} />
    </div>
  );
}

export function AuthShell({ children }) {
  const [brand, setBrand] = useState(null);

  useEffect(() => {
    api.get("/public/branding").then(({ data }) => setBrand(data)).catch(() => {});
  }, []);

  const name = brand?.name || "SilasTech Enterprise";
  const logoUrl = brand?.logo_url;
  const bg = brand?.login_bg_url;
  const overlay = (brand?.login_overlay ?? 75) / 100;
  const heading = brand?.login_heading || "Management System · STEMS";
  const tagline = brand?.login_tagline || brand?.tagline || "";
  const footer = brand?.login_footer || "@SilasTech Enterprise. Cameroon";
  const logoSize = brand?.login_logo_size ?? 80;
  const logoPos = brand?.login_logo_position || "center";

  const Logo = () => (
    logoUrl ? (
      <img src={logoUrl} alt={name} data-testid="auth-logo" style={{ width: logoSize, height: logoSize }}
        className="rounded-2xl object-contain bg-white/95 p-1.5 shadow-lg ring-1 ring-white/20" />
    ) : (
      <div data-testid="auth-logo" style={{ width: logoSize, height: logoSize }}
        className="rounded-2xl bg-gradient-to-br from-sky-400 to-blue-700 flex items-center justify-center font-heading font-extrabold text-white text-3xl shadow-lg">
        ST
      </div>
    )
  );

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0B132B]">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden">
        {bg ? (
          <>
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${bg})` }} data-testid="auth-bg" />
            <div className="absolute inset-0" style={{ backgroundColor: `rgba(11,19,43,${overlay})` }} />
          </>
        ) : (
          <>
            <div className="absolute inset-0" style={{ background: "linear-gradient(135deg,#0B132B 0%,#0f1e3d 55%,#0B132B 100%)" }} />
            <div className="absolute inset-0 opacity-20"
              style={{ background: "radial-gradient(600px circle at 20% 30%, #1D4ED8, transparent), radial-gradient(500px circle at 80% 70%, #0284C7, transparent)" }} />
            <div className="absolute inset-0 opacity-[0.06]"
              style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "42px 42px" }} />
          </>
        )}
        <div className={`relative flex-1 flex flex-col items-center text-center ${logoPos === "top" ? "justify-start pt-6" : "justify-center"}`}>
          <Logo />
          <div className="mt-6 font-heading font-extrabold text-white text-4xl leading-tight tracking-tight" data-testid="auth-brand-name">{name}</div>
          <div className="text-sm text-sky-300 tracking-wider uppercase mt-2" data-testid="auth-heading">{heading}</div>
          {tagline && <div className="text-sm text-slate-100/90 mt-4 max-w-xs" data-testid="auth-tagline">{tagline}</div>}
        </div>
        <div className="relative text-xs text-slate-400 text-center" data-testid="auth-footer">{footer}</div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 bg-[#F8FAFC]">
        <div className="w-full max-w-md">
          {/* Mobile brand header (brand panel is hidden on small screens) */}
          <div className="lg:hidden flex items-center gap-3 mb-6 justify-center">
            <div className="scale-90"><Logo /></div>
            <div className="font-heading font-extrabold text-slate-900 text-2xl tracking-tight">{name}</div>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}
